import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ArrowRight, Image, Minus, Pencil, Plus } from "lucide-react";
import { useFormContext } from "react-hook-form";
import {
  AD,
  AD_GROUP,
  ASSETS_GROUP,
  CAMPAIGN,
  CAMPAIGN_AGENT_SUMMARY_ADD_MANY,
  CAMPAIGN_AGENT_SUMMARY_ADD_ONE,
  CAMPAIGN_AGENT_SUMMARY_CREATE,
  CAMPAIGN_AGENT_SUMMARY_CREATE_UNNAMED,
  CAMPAIGN_AGENT_SUMMARY_CREATIVE,
  CAMPAIGN_AGENT_SUMMARY_CREATIVE_FOOTNOTE,
  CAMPAIGN_AGENT_SUMMARY_FOOTNOTE,
  CAMPAIGN_AGENT_SUMMARY_MORE_MANY,
  CAMPAIGN_AGENT_SUMMARY_MORE_ONE,
  CAMPAIGN_AGENT_SUMMARY_NEW_VALUE,
  CAMPAIGN_AGENT_SUMMARY_NEW_VALUES,
  CAMPAIGN_AGENT_SUMMARY_REMOVE_ITEM,
  CAMPAIGN_AGENT_SUMMARY_REMOVE_MANY,
  CAMPAIGN_AGENT_SUMMARY_REMOVE_ONE,
  CAMPAIGN_AGENT_SUMMARY_TITLE,
  NO,
  YES,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import {
  CAMPAIGN_AGENT_CHANGE_KIND,
  getOptimizationChangeSummary,
  humanizeFieldName,
} from "../../../utils/campaignAgentOptimizations";
import { ADS_KEYS_CREATIVES } from "../../../utils/constants";
import {
  EMPTY_VALUE,
  getOptimizationChangeCurrentValue,
} from "../../../pages/NewAdsCampaign/utils/getOptimizationChangeCurrentValue";

const ENTITY_KEY = {
  campaign: CAMPAIGN,
  adGroup: AD_GROUP,
  ad: AD,
  assetsGroup: ASSETS_GROUP,
};

const KIND_ICON = {
  [CAMPAIGN_AGENT_CHANGE_KIND.UPDATE]: Pencil,
  [CAMPAIGN_AGENT_CHANGE_KIND.ADD]: Plus,
  [CAMPAIGN_AGENT_CHANGE_KIND.REMOVE]: Minus,
  [CAMPAIGN_AGENT_CHANGE_KIND.CREATE]: Plus,
  [CAMPAIGN_AGENT_CHANGE_KIND.CREATIVE]: Image,
};

const SX_CLAMP_ONE_LINE = {
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
};

const CampaignAgentChangesSummary = ({
  optimization,
  showHeading = true,
  showFootnote = true,
}) => {
  const { t } = useTranslationApp();
  const { getValues } = useFormContext();
  const summary = getOptimizationChangeSummary({
    changes: optimization?.changes,
    creativeFields: ADS_KEYS_CREATIVES,
    getCurrentValue: (change) => {
      const currentValue = getOptimizationChangeCurrentValue(change, getValues);
      return currentValue === EMPTY_VALUE ? null : currentValue;
    },
  });

  const renderScalar = (preview) => {
    if (!preview) {
      return "";
    }
    if (preview.kind === "boolean") {
      return t(preview.value ? YES : NO);
    }
    return preview.text || "";
  };

  const renderDetail = (item) => {
    if (item.kind === CAMPAIGN_AGENT_CHANGE_KIND.CREATIVE) {
      return t(CAMPAIGN_AGENT_SUMMARY_CREATIVE);
    }

    if (item.kind === CAMPAIGN_AGENT_CHANGE_KIND.CREATE) {
      return item.createdName
        ? t(CAMPAIGN_AGENT_SUMMARY_CREATE, { name: item.createdName })
        : t(CAMPAIGN_AGENT_SUMMARY_CREATE_UNNAMED);
    }

    if (item.kind === CAMPAIGN_AGENT_CHANGE_KIND.ADD) {
      if ((item.itemsCount || 1) > 1) {
        return t(CAMPAIGN_AGENT_SUMMARY_ADD_MANY, { count: item.itemsCount });
      }
      return t(CAMPAIGN_AGENT_SUMMARY_ADD_ONE, {
        value: renderScalar(item.newPreview),
      });
    }

    if (item.kind === CAMPAIGN_AGENT_CHANGE_KIND.REMOVE) {
      const value = renderScalar(item.newPreview);
      if (value) {
        return t(CAMPAIGN_AGENT_SUMMARY_REMOVE_ONE, { value });
      }
      if ((item.itemsCount || 1) === 1) {
        return t(CAMPAIGN_AGENT_SUMMARY_REMOVE_ITEM);
      }
      return t(CAMPAIGN_AGENT_SUMMARY_REMOVE_MANY, {
        count: item.itemsCount,
      });
    }

    if (item.newPreview?.kind === "list") {
      return t(CAMPAIGN_AGENT_SUMMARY_NEW_VALUES, { count: item.itemsCount });
    }

    if (item.currentPreview) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            color: "text.secondary",
          }}
        >
          <Typography
            component="s"
            variant="body2"
            sx={{ ...SX_CLAMP_ONE_LINE, minWidth: 0 }}
          >
            {renderScalar(item.currentPreview)}
          </Typography>
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <ArrowRight size={12} />
          </Box>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ ...SX_CLAMP_ONE_LINE, minWidth: 0 }}
          >
            {renderScalar(item.newPreview)}
          </Typography>
        </Box>
      );
    }

    return t(CAMPAIGN_AGENT_SUMMARY_NEW_VALUE, {
      value: renderScalar(item.newPreview),
    });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: 2,
        bgcolor: "fields.background",
        p: 1.25,
      }}
    >
      {showHeading ? (
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {t(CAMPAIGN_AGENT_SUMMARY_TITLE)}
        </Typography>
      ) : null}
      {summary.items.map((item) => {
        const Icon = KIND_ICON[item.kind] || Pencil;
        const entityKey = ENTITY_KEY[item.entity];
        const entityLabel = entityKey ? t(entityKey) : item.entity || "";
        const fieldLabel = item.field
          ? t(item.field, { defaultValue: humanizeFieldName(item.field) })
          : "";
        const lineTitle =
          item.kind === CAMPAIGN_AGENT_CHANGE_KIND.CREATE
            ? entityLabel
            : `${entityLabel} · ${fieldLabel}`;
        const detail = renderDetail(item);

        return (
          <Box
            key={item.key}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              mt: 1,
            }}
          >
            <Box
              sx={{
                mt: "2px",
                color: "text.secondary",
                display: "flex",
                flexShrink: 0,
              }}
            >
              <Icon size={14} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, ...SX_CLAMP_ONE_LINE }}
              >
                {lineTitle}
              </Typography>
              {typeof detail === "string" ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={SX_CLAMP_ONE_LINE}
                >
                  {detail}
                </Typography>
              ) : (
                detail
              )}
            </Box>
          </Box>
        );
      })}
      {summary.hiddenCount > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t(
            summary.hiddenCount === 1
              ? CAMPAIGN_AGENT_SUMMARY_MORE_ONE
              : CAMPAIGN_AGENT_SUMMARY_MORE_MANY,
            { count: summary.hiddenCount }
          )}
        </Typography>
      ) : null}
      {showFootnote ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          {t(
            summary.hasCreativeChanges
              ? CAMPAIGN_AGENT_SUMMARY_CREATIVE_FOOTNOTE
              : CAMPAIGN_AGENT_SUMMARY_FOOTNOTE
          )}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CampaignAgentChangesSummary;
